import { API_BASE_URL } from '../config/env';
import { loyaltySession } from './session';
import {
  attemptLoyaltyAutoLogin,
  hasStoredLoyaltyAuthHints,
  redirectToLoyaltyLanding,
  shouldUseAggressiveRestore,
} from './loyaltySessionRestore';

const LOYALTY_LOGIN_PATHS = new Set(['/loyalty', '/loyalty/login', '/loyalty/auth']);

let probePromise = null;

export function isLoyaltyLoginEntryPath(pathname = window.location.pathname) {
  return LOYALTY_LOGIN_PATHS.has(pathname);
}

/** QR / camera opens often have no referrer and skip a full JS bootstrap on bfcache restore. */
export function isLikelyQrOrExternalOpen() {
  if (typeof window === 'undefined') {
    return false;
  }

  const nav = performance.getEntriesByType?.('navigation')?.[0];
  const emptyReferrer = !document.referrer;
  const externalNavigate = nav?.type === 'navigate' && emptyReferrer;

  return externalNavigate || Boolean(nav?.type === 'navigate' && nav?.deliveryType === 'navigate');
}

/**
 * Probe session once and hard-redirect on success.
 * Shared by index.js (before React) and the login page hook.
 */
export async function probeLoyaltySessionAndRedirect(search = window.location.search) {
  if (!isLoyaltyLoginEntryPath()) {
    return null;
  }

  if (probePromise) {
    return probePromise;
  }

  probePromise = (async () => {
    try {
      const user = await attemptLoyaltyAutoLogin({
        fromQrOrExternal: isLikelyQrOrExternalOpen(),
      });
      if (user) {
        redirectToLoyaltyLanding(search);
      }
      return user;
    } finally {
      probePromise = null;
    }
  })();

  return probePromise;
}

/**
 * Runs outside React so QR / bfcache restores still re-check stored tokens.
 * React StrictMode and cached login pages skip effects; this listener does not.
 */
export function installLoyaltyEarlyAuth() {
  if (typeof window === 'undefined') {
    return;
  }

  const run = () => {
    if (!isLoyaltyLoginEntryPath()) {
      return;
    }

    const shouldRun =
      hasStoredLoyaltyAuthHints() || shouldUseAggressiveRestore();

    if (!shouldRun) {
      return;
    }

    probeLoyaltySessionAndRedirect();
  };

  window.addEventListener('pageshow', run);
  window.addEventListener('focus', run);

  if (document.readyState === 'complete') {
    run();
  } else {
    window.addEventListener('load', run, { once: true });
  }
}

/** Direct fetch using stored token — used when modules may not be fully initialized yet. */
export async function fetchLoyaltySessionWithStoredToken() {
  let token = null;
  try {
    token = window.localStorage.getItem('loyalty_token');
  } catch {
    token = loyaltySession.getBearerToken();
  }

  if (!token) {
    return null;
  }

  const base = API_BASE_URL.replace(/\/$/, '');
  const response = await fetch(`${base}/loyalty/session`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json().catch(() => null);
  return data?.user ?? null;
}
